import React, { FC, useState } from "react";
import { Box } from "@material-ui/core";
import { Text } from "../components/Text";
import { withStyles } from "@material-ui/core/styles";
import MuiAccordion from "@material-ui/core/Accordion";
import { AccordionDetails } from "./AccordionDetails";
import { AccordionSummary } from "./AccordionSummary";

export const faqs = [
  {
    id: 1,
    question: "How does it work?",
    answer: `A constant directivity horn geometry is computed based on the user specifications. The approximate acoustical performance of the geometry is analysed using our in-house algorithm. Finally, the horn is combined with the selected driver to give the resulting on-axis frequency response.`,
  },
  {
    id: 2,
    question: "How accurate are the results?",
    answer: `All results are approximate, this a trade-off to improve calculation speed. The results still give a good reality check of what is achievable with the specified driver on a horn of a given size. 
    Directivity results will give a good indication of the frequency range where the horn is able to control directivity. The accuracy of the frequency response is good, as it relies on the measured performance of the compression drivers.`,
  },
  {
    id: 3,
    question: "Will a horn designed using the app be optimal?",
    answer: `Very unlikely. Optimum performance requires a skilled horn designer to optimise the design and make judgments, balancing trade-offs for the specific application in a way that an automatic algorithm can’t do.`,
  },
  {
    id: "maxLength",
    question: "What does the 'MAX LENGTH' parameter do?",
    answer: `The length of the horn depends on the mouth size and coverage angles. If the specified MAX LENGTH is exceeded, the mouth size is reduced to match the specified length.`,
  },
  {
    id: 4,
    question: "What is 'BEAMWIDTH'?",
    answer: `Beamwidth is the total coverage angle of the horn, specified for the horizontal and vertical planes. The SPL at the specified angle is 6dB down from the maximum value.`,
  },
  {
    id: 5,
    question: "What is 'DIRECTIVITY INDEX'?",
    answer: `Directivity Index (DI) can be thought of as the gain of the horn due to directionality or focusing of sound in a particular direction.`,
  },
  {
    id: "upperFrequency",
    question: "What is the 'UPPER FREQUENCY' for?",
    answer: `This is the highest frequency where the horn will be able to control directivity in the plane of widest dispersion. Above this frequency the beamwidth of the horn will decrease. For a horn with a diffraction slot, this frequency, together with the desired dispersion angle, determines the width of the slot. For a horn without a diffraction slot, the upper frequency is determined by the throat size.`,
  },
  {
    id: 7,
    question: "Is there a lower cut-off frequency?",
    answer: `The lower cut-off frequency is determined by how rapidly the horn flare expands, and the geometry of the throat section (if used). Both these factors are determined by specified beamwidth angles and upper frequency, therefore the lower cut-off frequency can’t be independently specified.`,
  },
  {
    id: "diffractionGrating",
    question: "What does the 'ALLOW DIFFRACTION SLOT' option do?",
    answer: `If this option is selected, the horn is allowed to have a diffraction slot. The width of the slot is determined by coverage angle and UPPER FREQUENCY. This will introduce a throat section in the horn. The result is often that the horn is able to control directivity to a higher frequency than without the diffraction slot, but at the same time, resonances may be introduced.`,
  },
  {
    id: "prioritiseLoading",
    question: "What does the 'PRIORITISE LOADING' option do?",
    answer: `This option attempts to introduce a diffraction slot and throat section that is optimally matched to the flaring section of the horn. The algorithm tries to do this in a way that produces a smooth acoustic load. It is not always possible to achieve this goal. Also, in some cases the height or width specifications may be exceeded in order to improve the acoustic load. The limitations are described in the paper “Limitations of Single-Surface Horns for Directivity Control” by Bjørn Kolbrek, AES convention paper #10334.`,
  },
  {
    id: 10,
    question: "What does 'LOADING' mean?",
    answer: `In the same way as a loudspeaker is the electrical load of the amplifier, the horn is the acoustical load of the compression driver. And in the same way as the impedance of a loudspeaker varies with frequency, so will the acoustic load presented by the horn. If the load varies too much, the resulting frequency response will be uneven.`,
  },
  {
    id: 11,
    question: "What happens if the parameters I put in will not work together?",
    answer: `The algorithm will always attempt to produce a horn, but the result may not have dimensions or acoustic performance that work well in the intended application.`,
  },
  {
    id: 12,
    question: "Do some parameters have priority over others?",
    answer: `Yes. The algorithm will first look at the widest specified beamwidth and the maximum dimension in the corresponding plane. The first two horn walls will be computed based on these specifications. If the resulting length exceeds the maximum length limit, the mouth dimension will be reduced.
    If a diffraction slot is allowed, the two other horn walls will be determined based on the specified beamwidth and dimension in the other plane, also limited by the maximum length. If no diffraction slot is desired, only the specified beamwidth in the second plane is taken into account. The size is determined based on geometry, still respecting the mouth dimensions and maximum lengths as upper limits.
    If Prioritise Loading is checked, the size in the plane of narrowest beamwidth will be adjusted to give a throat section with good acoustic loading characteristics. The maximum length is not taken into account, as this could prevent the algorithm finding a good design.`,
  },
  {
    id: 13,
    question: "Does the app work for bass horns?",
    answer: `The horns produced by the app are intended for high frequency directivity control and will not be suitable as bass horns.`,
  },
  {
    id: 14,
    question: "Does the app work for throat sizes other than 1”?",
    answer: `The app adapts to the exit size of the specified compression driver.`,
  },
  {
    id: "viewHorn",
    question: "Can I view the horn from different angles?",
    answer: `You are able to rotate the horn by holding your left mouse button and dragging your mouse in any direction you choose, giving you a full view of the horn you’ve created. You can also use your scroll wheel to zoom in and out of the horn to view it at different scales.`,
  },
  {
    id: 109,
    question: "Why can’t I log in?",
    answer:
      "If you have never created an account before then you need to register. Otherwise your password may be wrong, try reset it.",
  },
  {
    id: 110,
    question: "I forgot my password.",
    answer:
      "On both the login screen and account screen you will have the option to generate a new password.",
  },
  {
    id: 111,
    question: "How do I see designs I’ve already made?",
    answer:
      "Once you create an account, you will have access to a page which shows you all your previous designs.",
  },
  {
    id: 112,
    question: "Can I edit existing designs or do I have to create a new one?",
    answer:
      "You can create as many new designs as you like! If you have an account they will be saved to view again later.",
  },
  {
    id: 113,
    question: "Why can’t I export the horn geometry?",
    answer: `The horns are automatically generated by a simple algorithm and are not optimised. The simulation method uses approximations in order to optimise speed, and for that reason leaves out details that may be important in certain applications. Some horns will also show complex behaviour that is not captured. (See also “How accurate are the results?”) Celestion can therefore not guarantee that the horns will perform exactly like the simulations, and do not recommend using the generated profiles directly without verification. Instead we recommend that the Horn Wizard is used as a tool for initial investigation and specification, followed by manual optimisation for the application at hand using more advanced simulation tools.`,
  }
];

export const FaqAccordion: FC = () => {
  const [expanded, setExpanded] = useState<number | string | false>(false);

  const handleChange =
    (panel: number | string) =>
    (event: React.ChangeEvent<{}>, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  return (
    <Box>
      {faqs.map((faq) => (
        <Accordion
          key={faq.id}
          expanded={expanded === faq.id}
          onChange={handleChange(faq.id)}
        >
          <AccordionSummary>
            <Text color="secondary" bold>
              {faq.question}
            </Text>
          </AccordionSummary>
          <AccordionDetails>
            <Text>{faq.answer}</Text>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

const Accordion = withStyles({
  root: {
    border: "1px solid grey",
    boxShadow: "none",
    backgroundColor: "white",
    "&:nth-child(even)": {
      backgroundColor: "#F2F2F2",
    },
    "&:first-child": {
      borderRadius: "10px 10px 0px 0px",
    },
    "&:last-child": {
      borderRadius: "0px 0px 10px 10px",
    },
    "&:not(:last-child)": {
      borderBottom: 0,
    },
    "&:before": {
      display: "none",
    },
    "&$expanded": {
      margin: "auto",
    },
  },
  expanded: {},
})(MuiAccordion);
